import { useQueryClient } from '@tanstack/react-query';
import { Outlet, createFileRoute, useRouter } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AppSidebar } from '@/components/layout/app-sidebar.js';
import { RouteErrorBoundary } from '@/components/shared/route-error-boundary.js';
import { SidebarInset, SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar.js';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip.js';
import { apiRequest } from '@/lib/api/client.js';
import { isUnauthenticated, redirectToSignIn, useSession } from '@/features/auth/session.js';
import { trackSessionActivity } from '@/lib/session-activity.js';
import { scheduleSessionExpiry } from '@/lib/session-expiry.js';

/**
 * Rule CR2 (ported from the old shell): a boundary at the `_app` layout
 * itself, scoped to re-navigating to the current URL rather than a full
 * reload, so a failure in the shell does not force the user to redo filters
 * or navigation.
 */
function AppErrorBoundary({ error }: { error: unknown }) {
  const router = useRouter();
  return (
    <RouteErrorBoundary
      error={error}
      boundary="app-shell"
      onRetry={() => void router.invalidate()}
    />
  );
}

function AppLayout(): React.JSX.Element | null {
  const { t } = useTranslation('layout');
  const session = useSession();
  const queryClient = useQueryClient();

  // unified-access.md section 3.1 (SH-28): once the time left that the server
  // reported has passed and no save is in flight, ask the server again. Only
  // its 401 ends the session (the effect below then returns to Unified Access
  // with this page); a still-valid answer re-arms the timer. The device clock
  // is never compared with the server's expiry time.
  const { refetch, dataUpdatedAt } = session;
  const expiresInMs = session.data?.expiresInMs;
  useEffect(
    () =>
      scheduleSessionExpiry(
        expiresInMs,
        dataUpdatedAt,
        () => void refetch(),
        () => queryClient.isMutating() > 0,
      ),
    [expiresInMs, dataUpdatedAt, refetch, queryClient],
  );

  // unified-access.md section 3.1 (KAN-50, SH-29): the employee's clicks and
  // key presses keep the session open; reads and refetches never do.
  const signedIn = !!session.data;
  useEffect(
    () =>
      signedIn
        ? trackSessionActivity(() => apiRequest<void>('/auth/activity', { method: 'POST' }))
        : undefined,
    [signedIn],
  );

  useEffect(() => {
    // unified-access.md section 3, step 5 / ui-contract.md section 6: no
    // session sends the browser to Unified Access with a return address.
    // platform-launch-contract.md ("returnTo") confirms UA accepts an
    // absolute platform URL here and validates its origin against the
    // platform's own registered webUrl server-side before ever using it, so
    // the current page is carried through untouched (SH-18 bug 4).
    if (!session.isPending && isUnauthenticated(session.error)) {
      redirectToSignIn();
    }
  }, [session.isPending, session.error]);

  if (session.isPending) return null;
  if (isUnauthenticated(session.error)) return null;

  if (session.error) {
    throw session.error;
  }

  return (
    <SidebarProvider>
      <AppSidebar user={session.data} />
      <SidebarInset className="min-w-0">
        <header className="flex h-16 shrink-0 items-center gap-2 px-4">
          <Tooltip>
            <TooltipTrigger asChild>
              <SidebarTrigger className="-ms-1" />
            </TooltipTrigger>
            <TooltipContent side="bottom">{t('nav.toggleSidebarHint')}</TooltipContent>
          </Tooltip>
        </header>
        <main className="flex min-w-0 flex-1 flex-col gap-6 p-4 pt-0 md:p-6 md:pt-0">
          <Outlet />
        </main>
      </SidebarInset>
    </SidebarProvider>
  );
}

export const Route = createFileRoute('/_app')({
  component: AppLayout,
  errorComponent: AppErrorBoundary,
});
